import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { formatCurrency } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useActiveCurrency } from "@/lib/useCurrency";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowUpRight, Inbox, Users2, Target, DollarSign, Activity, TrendingUp, TrendingDown, Minus, Banknote, Flame } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const currency = useActiveCurrency();
  const { data, isLoading } = useQuery({ queryKey: ["overview"], queryFn: () => api.get("/analytics/overview").then((r) => r.data) });
  const { data: convs } = useQuery({ queryKey: ["convs-recent"], queryFn: () => api.get("/inbox/conversations").then((r) => r.data.slice(0, 5)) });
  const { data: leads } = useQuery({ queryKey: ["leads-recent"], queryFn: () => api.get("/leads?status=queue").then((r) => r.data.slice(0, 5)) });
  const { data: cashWeek } = useQuery({ queryKey: ["cash-week"], queryFn: () => api.get("/analytics/cash-week").then((r) => r.data), refetchInterval: 60_000 });

  return (
    <div>
      <PageHeader
        overline="Overview"
        title={`Welcome back, ${user?.name?.split(" ")[0] || "there"}.`}
        subtitle={user?.role === "company_admin" ? "Company-wide performance across all divisions." : "Your division at a glance."}
      />

      <div className="px-6 lg:px-10 pb-10">
        {/* North star + core metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {isLoading ? (
            [...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-sm bg-secondary/50" />)
          ) : (
            <>
              <StatCard
                overline="North star"
                title="Potential Pipeline"
                value={formatCurrency(data?.pipeline_value, currency)}
                icon={DollarSign}
                highlight
              />
              <StatCard overline="Won" title="Total Revenue Won" value={formatCurrency(data?.total_won_value, currency)} icon={TrendingUp} />
              <StatCard overline="Deals" title="Avg Deal Value" value={formatCurrency(data?.avg_deal_value, currency)} icon={Target} />
              <StatCard overline="Speed" title="Avg Time to Close" value={`${Math.round(data?.avg_time_to_close_days || 0)}d`} icon={Activity} />
            </>
          )}
        </div>

        {/* Funnel Conversion */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
          <FunnelStep n="1" label="Inquiry → Lead" value={`${((data?.inquiries_to_leads_ratio || 0) * 100).toFixed(0)}%`} sub={`${data?.total_lead_inquiries || 0} of ${data?.total_inquiries || 0} inquiries tagged Lead`} />
          <FunnelStep n="2" label="Lead → Deal" value={`${((data?.leads_to_deals_ratio || 0) * 100).toFixed(0)}%`} sub={`${data?.leads_converted || 0} of ${data?.total_leads || 0} leads converted`} />
          <FunnelStep n="3" label="Deal → Closed Won" value={`${((data?.deals_to_close_ratio || 0) * 100).toFixed(0)}%`} sub={`${data?.deals_won || 0} of ${data?.total_deals || 0} deals won`} />
        </div>

        {/* Cash Collected This Week — mini live widget */}
        <CashCollectedThisWeek data={cashWeek} currency={currency} />

        {/* Recent lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <Card className="rounded-sm border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Unified Inbox</div>
                <div className="font-display text-xl font-extrabold tracking-tight">Recent Conversations</div>
              </div>
              <Link to="/inbox" className="text-xs uppercase tracking-widest text-primary hover:underline">Open <ArrowUpRight className="inline h-3 w-3" /></Link>
            </div>
            <div className="space-y-2">
              {(convs || []).map((c) => (
                <Link key={c.id} to="/inbox" className="flex items-center gap-3 p-3 rounded-sm hover:bg-secondary/60 transition-colors border border-transparent hover:border-border">
                  <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">
                    {c.contact?.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{c.contact?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.last_message?.text || c.subject}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.channel}</div>
                </Link>
              ))}
              {!convs?.length && <div className="text-sm text-muted-foreground py-6 text-center">No conversations yet. Try the Inbox → Simulate.</div>}
            </div>
          </Card>

          <Card className="rounded-sm border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Leads Queue</div>
                <div className="font-display text-xl font-extrabold tracking-tight">Waiting for Qualification</div>
              </div>
              <Link to="/leads" className="text-xs uppercase tracking-widest text-primary hover:underline">Open <ArrowUpRight className="inline h-3 w-3" /></Link>
            </div>
            <div className="space-y-2">
              {(leads || []).map((l) => (
                <div key={l.id} className="flex items-center gap-3 p-3 rounded-sm hover:bg-secondary/60 border border-transparent hover:border-border">
                  <div className="h-9 w-9 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs font-bold text-yellow-500">
                    {l.contact?.name?.[0]?.toUpperCase() || "L"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.contact?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.source_channel} · {l.contact?.email || l.contact?.phone || ""}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-yellow-500">Queue</div>
                </div>
              ))}
              {!leads?.length && <div className="text-sm text-muted-foreground py-6 text-center">No leads waiting. Tag an inbox message as “Lead” to fill the queue.</div>}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StatCard({ overline, title, value, icon: Icon, highlight }) {
  return (
    <Card className={`rounded-sm border-border p-6 relative overflow-hidden ${highlight ? "bg-primary/10 border-primary/40" : "bg-card"}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{overline}</div>
        <Icon className={`h-4 w-4 ${highlight ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="mt-6">
        <div className="text-sm text-muted-foreground">{title}</div>
        <div className={`font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-1 ${highlight ? "text-white" : ""}`}>{value}</div>
      </div>
    </Card>
  );
}

function FunnelStep({ n, label, value, sub }) {
  return (
    <Card className="rounded-sm border-border bg-card p-6 relative overflow-hidden">
      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">Step {n}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      </div>
      <div className="font-display text-5xl font-extrabold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-2">{sub}</div>
      <div className="absolute inset-x-0 bottom-0 h-1 bg-primary/40" style={{ width: value }} />
    </Card>
  );
}

function CashCollectedThisWeek({ data, currency }) {
  // Always render even before data arrives so the layout doesn't shift
  const current = data?.current || { collected: 0, invoice_count: 0, from: "", to: "" };
  const previous = data?.previous || { collected: 0 };
  const delta = data?.delta || { absolute: 0, pct: null };
  const daily = data?.daily || [];
  const streak = data?.streak || { current: 0, best: 0 };
  const cur = data?.currency || currency || "SGD";
  const pct = delta.pct;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  const arrowClass = up ? "text-green-400" : down ? "text-red-400" : "text-muted-foreground";
  const ArrowIcon = up ? TrendingUp : down ? TrendingDown : Minus;

  // Deep-link to a filtered invoice list keyed on the current 7-day window
  const drillTo = current.from && current.to
    ? `/invoices?paid_from=${current.from}&paid_to=${current.to}`
    : "/invoices";

  // Flame intensity — more days = louder flame
  const flameLit = streak.current > 0;
  const flameGlow = streak.current >= 3;

  return (
    <Link
      to={drillTo}
      className="block mb-8 group"
      data-testid="cash-this-week-widget-link"
    >
      <Card
        className="rounded-sm border-border bg-card p-6 relative overflow-hidden transition-all hover:border-green-400/50 hover:bg-green-500/5 cursor-pointer"
        data-testid="cash-this-week-widget"
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">
              <Banknote className="h-3.5 w-3.5" /> Cash flow · Live
            </div>
            <div className="font-display text-xl font-extrabold tracking-tight mt-1 group-hover:text-green-400 transition-colors">Cash collected this week</div>
            <div className="text-xs text-muted-foreground mt-1">
              {current.from && current.to ? `${current.from} → ${current.to}` : "Rolling last 7 days"}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                View invoices <ArrowUpRight className="inline h-3 w-3" />
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div>
              <div
                className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-green-400"
                data-testid="cash-week-amount"
              >
                {formatCurrency(current.collected, cur)}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                {current.invoice_count || 0} payment{current.invoice_count === 1 ? "" : "s"}
              </div>
            </div>

            {/* Streak flame */}
            <div
              className={`flex items-center gap-1.5 rounded-sm px-2.5 py-1.5 border ${
                flameLit
                  ? "border-orange-400/40 bg-orange-500/10"
                  : "border-border bg-secondary/30 opacity-60"
              }`}
              data-testid="cash-week-streak"
              title={
                flameLit
                  ? `${streak.current} day${streak.current === 1 ? "" : "s"} in a row · best ${streak.best}`
                  : "No streak yet — collect a payment today to start one"
              }
            >
              <Flame
                className={`h-5 w-5 ${flameLit ? "text-orange-400" : "text-muted-foreground"} ${
                  flameGlow ? "drop-shadow-[0_0_6px_rgba(251,146,60,0.8)]" : ""
                }`}
                fill={flameLit ? "currentColor" : "none"}
                data-testid="cash-week-flame"
              />
              <div className="leading-tight">
                <div className="font-display text-lg font-black text-foreground">
                  {streak.current || 0}
                </div>
                <div className="text-[8px] uppercase tracking-widest text-muted-foreground -mt-0.5">
                  day{streak.current === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-2 rounded-sm px-3 py-2 border border-border bg-secondary/30 ${arrowClass}`}
            data-testid="cash-week-trend"
          >
            <ArrowIcon className="h-5 w-5" />
            <div>
              <div className="font-display text-lg font-black leading-none">
                {pct == null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">
                vs prior 7 days
              </div>
            </div>
          </div>
        </div>

        {/* Daily sparkline */}
        <DailySparkline daily={daily} currency={cur} />

        <div className="mt-3 text-xs text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            Previous window collected <span className="text-foreground font-semibold">{formatCurrency(previous.collected, cur)}</span>
          </span>
          {pct != null && (
            <span>
              Change <span className={`font-semibold ${arrowClass}`}>{delta.absolute > 0 ? "+" : ""}{formatCurrency(delta.absolute, cur)}</span>
            </span>
          )}
          {streak.best > 0 && (
            <span className="text-orange-400/80" data-testid="cash-week-best-streak">
              Best streak: <span className="font-semibold">{streak.best} day{streak.best === 1 ? "" : "s"}</span>
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

function DailySparkline({ daily, currency }) {
  // Days without payments still render as a flat baseline so the axis matches
  // the calendar. Days with cash draw a bar sized to the max of the window.
  const maxAmt = Math.max(1, ...daily.map((d) => d.amount || 0));
  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dayLabel = (iso) => {
    if (!iso) return "";
    try {
      const dt = new Date(iso + "T00:00:00");
      return DAY_LABELS[(dt.getDay() + 6) % 7]; // Mon-first
    } catch {
      return "";
    }
  };
  const BAR_HEIGHT = 44; // px — matches ~h-11
  return (
    <div className="mt-5 pt-4 border-t border-border" data-testid="cash-week-sparkline">
      <div className="flex gap-2 items-end">
        {daily.map((d) => {
          const hasCash = (d.amount || 0) > 0;
          const px = hasCash ? Math.max(4, Math.round((d.amount / maxAmt) * BAR_HEIGHT)) : 2;
          return (
            <div
              key={d.date}
              className="flex-1 flex flex-col items-center gap-1 min-w-0"
              title={`${d.date}: ${new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "SGD" }).format(d.amount || 0)} · ${d.count || 0} payment${d.count === 1 ? "" : "s"}`}
              data-testid={`cash-week-day-${d.date}`}
            >
              <div
                className="w-full flex items-end justify-center"
                style={{ height: `${BAR_HEIGHT}px` }}
              >
                <div
                  className={`w-full rounded-t-sm transition-all ${
                    hasCash ? "bg-green-500/80" : "bg-secondary/40"
                  }`}
                  style={{ height: `${px}px` }}
                />
              </div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground">
                {dayLabel(d.date)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
